import { STORAGE_LOCKER } from 'spw-constants';
import {
  IChildWorksheetSpaceType,
  IWorksheetRow,
} from 'spw-redux/modules/calculators/types';
import {
  IProjectSettings,
  RatioCalculationType,
} from 'spw-redux/modules/settings/types';
import { PlainProgramScenario } from './Scenario';
import WorksheetRow from './WorksheetRow';

const ADDITIONAL_CLEARANCE_FOR_UNIT_FACTOR = 2;

export default class WorksheetStorageSpaceRow extends WorksheetRow {
  lockersOrDrawersQuantity: number;

  lockersOrDrawersPerUnit: number;

  isStorageSupportSpace = true;

  isIncludedInTotalWorkSeats = false;

  storageUnitsRatio: number;

  lockersOrDrawersRatioLabel: string;

  storageUnitQuantityLabel: string;

  storageUnitRatioLabel: string;

  storageTypeLabel: string;

  dividendForRatio: number;

  usableSquareInches: number;

  isLocker: boolean;

  private readonly projectSettings: IProjectSettings;

  constructor(
    row: IWorksheetRow,
    scenario: PlainProgramScenario,
    projectSettings: IProjectSettings,
    worksheetId?: number
  ) {
    super(row, worksheetId);
    this.projectSettings = projectSettings;

    this.isLocker = row.spaceType.spaceType === STORAGE_LOCKER;

    const furnitureTypeLabel = this.isLocker ? 'Locker Banks' : 'Cabinets';
    const storageTypeLabel = this.isLocker ? 'Lockers' : 'Drawers';

    const {
      projectStorageConfigRatio,
      dividendForRatio,
      ratioLabel,
    } = this.getProjectStorageConfig(
      {
        isLocker: this.isLocker,
      },
      scenario
    );

    this.lockersOrDrawersRatioLabel = `${storageTypeLabel}:${ratioLabel}`;
    this.storageUnitRatioLabel = `${furnitureTypeLabel.replace(
      'Locker ',
      ''
    )}:${ratioLabel}`;
    this.storageUnitQuantityLabel = `Quantity of ${furnitureTypeLabel}`;
    this.dividendForRatio = dividendForRatio;
    this.storageTypeLabel = storageTypeLabel;
    this.lockersOrDrawersQuantity = projectStorageConfigRatio * row.quantity;
    this.lockersOrDrawersPerUnit = (row.spaceType as IChildWorksheetSpaceType).unitsHigh;

    this.quantity = this.parseLockerOrDrawersQuantityToStorageUnits();

    this.storageUnitsRatio = dividendForRatio / this.quantity;
    this.usableSquareInches = row.usf;
    this.usf =
      this.parseSquareInchesToSquareFoot(row.usf) *
      ADDITIONAL_CLEARANCE_FOR_UNIT_FACTOR;
    this.totalUsf = this.usf * this.quantity;
  }

  parseLockerOrDrawersQuantityToStorageUnits = () => {
    return this.lockersOrDrawersQuantity / this.lockersOrDrawersPerUnit;
  };

  parseSquareInchesToSquareFoot = (usableSquareInches: number) => {
    return usableSquareInches / 144;
  };

  getProjectStorageConfig = (
    { isLocker }: { isLocker: boolean },
    scenario: PlainProgramScenario
  ) => {
    switch (
      isLocker
        ? this.projectSettings?.lockerRatioCalculationType
        : this.projectSettings?.fileCabinetRatioCalculationType
    ) {
      case RatioCalculationType.HEADCOUNT:
        return {
          projectStorageConfigRatio: scenario.headcount / scenario.workSeats,
          ratioLabel: `HC Ratio`,
          dividendForRatio: scenario.headcount,
        };
      case RatioCalculationType.HEADCOUNT_WITH_UTILIZATION:
        return {
          projectStorageConfigRatio:
            scenario.headcountWithUtilization / scenario.workSeats,
          ratioLabel: `HC Ratio`,
          dividendForRatio: scenario.headcountWithUtilization,
        };
      case RatioCalculationType.HEADCOUNT_WITH_VACANCY:
        return {
          projectStorageConfigRatio:
            scenario.headcountWithVacancy / scenario.workSeats,
          ratioLabel: `HC Ratio`,
          dividendForRatio: scenario.headcountWithVacancy,
        };
      default:
        return {
          projectStorageConfigRatio: 1,
          ratioLabel: `Seats Ratio`,
          dividendForRatio: scenario.workSeats,
        };
    }
  };

  static toWorksheetRow = (
    row: Partial<WorksheetStorageSpaceRow>,
    totalWorkSeats: number
  ): WorksheetRow =>
    ({
      ...row,
      quantity:
        (row.lockersOrDrawersQuantity * (totalWorkSeats || 1)) /
        (row.dividendForRatio || 1),
      usf: row.usableSquareInches,
    } as WorksheetRow);
}
